import { Box, CircularProgress, Fade, MenuItem, TextField } from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import 'whatwg-fetch';

import { useAuthsState } from '../../app/auths/AuthsContext';
import { SET_NOTIFICATIONS, useMainDispatch, useMainState } from "../../contexts/MainContext";
import {
  SET_DATABASE,
  SET_DESCRIPTION_OBJECT,
  SET_IS_AWAKE,
  usePrimusDispatch,
  usePrimusState
} from "../../contexts/PrimusContext";
import { RESET_TABLE_STATE, useTableDispatch } from "../../contexts/TableContext";
import { EkulturAPI } from '../../services/EkulturAPI';
import { PrimusApi } from "../../services/PrimusApi";

export const DatabaseSelector = () => {
  const [availableServers, setAvailableServers] = useState([]);
  const [selectedServer, setSelectedServer] = useState();
  const [loading, setLoading] = useState(true);

  const dispatch = usePrimusDispatch();
  const tableDispatch = useTableDispatch();
  const dispatchCallback = useCallback(dispatch, [dispatch]);
  const tableDispatchCallback = useCallback(tableDispatch, [tableDispatch]);
  const mainDispatch = useMainDispatch();
  const mainDispatchCallback = useCallback(mainDispatch, [mainDispatch]);

  const { notifications } = useMainState();
  const { t } = useTranslation('translations');
  const { userData } = useAuthsState();
  const { baseUrl, isAwake } = usePrimusState();

  const dispatchError = (transKey, error) => {
    mainDispatchCallback({
      type: SET_NOTIFICATIONS,
      notifications: [...notifications,
        {
          type: 'error',
          text: t(transKey),
          error: error?.message || error
        }
      ]
    });
  };

  useEffect(() => {
    if (baseUrl !== '') {
      let primus = new PrimusApi(baseUrl);

      primus.createDescriptionObject().then((descriptionObject) => {
        dispatchCallback({
          type: SET_DESCRIPTION_OBJECT,
          descriptionObject: descriptionObject
        });
      }).catch((e) => console.error(t('table.default.jobs.errorDatabaseContextData'), e));
    }
  }, [baseUrl])

  useEffect(() => {
    const api = new EkulturAPI();

    if (!availableServers.length) {
      api.getAvailablePrimusInstances(userData?.uniqueId).then((res) => {
        let servers = res ?? [];
        if (!userData?.appAccess?.apps?.find(app => app.id === window._env_.REACT_APP_ID)) {
          let museums = userData?.appAccess?.museums;
          if (museums?.length) {
            servers = servers.filter((s) => {
              let museum = museums.find(m => m.id === s.id);
              return museum?.applications?.find(app => app.id === window._env_.REACT_APP_ID);
            }).sort((a, b) => a.name.localeCompare(b.name));
          } else {
            servers = [];
          }
        }
        if (window.location.host.startsWith('localhost')) {
          servers.push({
            id: -1,
            name: 'Localhost',
            serverUrl: 'http://localhost:5000'
          });
        }
        setAvailableServers(servers);
        setSelectedServer(servers.find((s) => baseUrl === s.serverUrl) || servers.find((s) => !!s.serverUrl));
      }).finally(() => {
        setLoading(false);
      }).catch((e) => dispatchError('table.default.jobs.errorGettingInstances', e));
    } else {
      setLoading(false);
    }
  }, [userData])

  const handleSelectDatabase = (event) => {
    if (event.target.value !== selectedServer.serverUrl) {
      let server = availableServers.find((s) => s.serverUrl === event.target.value)
      setSelectedServer(server)
      mainDispatchCallback({
        type: SET_NOTIFICATIONS,
        notifications: []
      });
      tableDispatchCallback({ type: RESET_TABLE_STATE });
    }
  }

  useEffect(() => {
    if (!selectedServer) return;
    dispatchCallback({
      type: SET_DATABASE,
      baseUrl: selectedServer.serverUrl,
      database: selectedServer.name,
      versionMajor: -1,
      versionMinor: -1,
    });
    dispatchCallback({
      type: SET_IS_AWAKE,
      isAwake: false
    });
  }, [selectedServer]);

  useEffect(() => {
    if (!isAwake && !!baseUrl) {
      let primus = new PrimusApi(baseUrl);
      let retryNr = 0;
      const interval = setInterval(async () => {
        if (retryNr > 20) {
          clearInterval(interval);
        }
        const res = await primus.getIsAwake();
        retryNr += 1;
        dispatchCallback({
          type: SET_IS_AWAKE,
          isAwake: !!res,
          retryNr: retryNr
        });
        if (!!res) clearInterval(interval);
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [baseUrl, isAwake]);

  return (
    <>
      {!loading ? (
        <Fade in={true} unmountOnExit={true}>
          <Box sx={{
            padding: '0 .5rem',
          }}>
            <TextField
              label={t('menu.washer.selectPrimusServer')}
              disabled={!availableServers.length}
              fullWidth
              onChange={handleSelectDatabase}
              select
              value={selectedServer?.serverUrl}
            >
              {availableServers.map((server) => (
                <MenuItem key={server.id} value={server.serverUrl} disabled={!server.serverUrl}>
                  {server.name}
                </MenuItem>
              ))
              }
            </TextField>
          </Box>
        </Fade>
      ) : (
        <Fade in={true} unmountOnExit={true}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              padding: '8px 10px',
              width: '100%'
            }}
          >
            <CircularProgress />
          </Box>
        </Fade>
      )}
    </>
  )
}